/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>4 - ES6 классы</h2>
    <p className={styles.description}>
      JavaScript является единственным массовым языком программирования с наследованием, основанным не на классах, а на
      прототипах. Огромное число разработчиков, владеющих техникой использования классов, постоянно пытались переделать
      JavaScript под свои умения, а главное, под имеющиеся наработки в области ООП. В ES6 сделали шаг навстречу и ввели
      класс похожий на таковой в языках Java, C# и т. п. Это все те же прототипы, только в красивой обертке.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.1 - Объявление класса</h3>
      <p className={styles.description}>
        <b>Класс</b> — это удобный способ для задания конструктора вместе с прототипом.{' '}
      </p>
      <p className={styles.description}>Преимущества использования классов: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>Весь код внутри класса выполняется в строгом режиме.</li>
        <li className={styles.list__item}>Все методы являются неперечислимыми.</li>
        <li className={styles.list__item}>
          У всех методов класса отсутствует внутренний метод <code className={styles.code}>[[Construct]]</code>, что
          означает появление ошибки при попытке использовать эти методы с оператором{' '}
          <code className={styles.code}>new</code>.
        </li>
        <li className={styles.list__item}>
          Вызов конструктора класса без оператора <code className={styles.code}>new</code> вызовет ошибку.
        </li>
      </ul>
      <p className={styles.description}>
        Предположим, мы запустили отель и хотим, чтобы объект со свойсвами и методами представлял каждого гостя.{' '}
      </p>
      <CopyBlock
        text={`//class declaration
class Guest {
  //...
}

// Под капотом класс это функция-конструктор с прототипом
console.log(typeof Guest); // "function"
console.log(Guest.prototype); // {constructor: ƒ}

const guest = new Guest();
console.log(guest); // Guest {}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Как строить класс, зависит от того, что вам нужно. В нашем случае, класс представляет собой гостя отеля, поэтому
        мы добавим туда поля для имени и номера комнаты.{' '}
      </p>
      <p className={styles.description}>
        Класс использует аналог функции-конструктора — метод <code className={styles.code}>constructor</code>. Это
        особый метод, он должен присутствовать в объявлении класса обязательно. Назначение конструктора — создавать
        собственные свойства экземпляра класса.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Функция <code className={styles.code}>constructor</code> запускается при вызове{' '}
          <code className={styles.code}>new Guest</code>, остальные методы записываются в{' '}
          <code className={styles.code}>Guest.prototype</code>.
        </li>
        <li className={styles.list__item}>
          Свойство <code className={styles.code}>prototype</code> класса доступно только для чтения, т.е. это свойство
          нельзя изменить.
        </li>
        <li className={styles.list__item}>В классе должен быть только один конструктор, иначе будет ошибка.</li>
      </ul>
      <CopyBlock
        text={`class Guest {
  // аналог функции-конструктора
  constructor(name, roomNumber) {
    this.name = name;
    this.roomNumber = roomNumber;
  }
}

const mango = new Guest('Mango', 26);

console.log(mango); // {name: Mango, roomNumber: 26}
console.log(mango instanceof Guest); // true
console.log(mango instanceof Object); // true`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.2 - Методы</h3>
      <p className={styles.description}>
        Добавим функцию <code className={styles.code}>getFullInfo</code>. Функции определенные таким образом называться{' '}
        <b>методами класса</b> и доступны всем экземплярам через <code className={styles.code}>prototype</code>.{' '}
      </p>
      <CopyBlock
        text={`class Guest {
  // Аналог функции-конструктора
  constructor(name, roomNumber) {
    this.name = name;
    this.roomNumber = roomNumber;
  }

  // Аналог Guest.prototype.getFullInfo
  getFullInfo() {
    console.log(\`
      Guest $ {this.name}
      Room number $ {this.roomNumber}
    \`);
  }
}

const mango = new Guest('Mango', 26);

mango.getFullInfo();
// Guest Mango
// Room number 26`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.3 - Геттеры и сеттеры</h3>
      <p className={styles.description}>
        Собственные свойства класса желательно хранить в <code className={styles.code}>constructor</code>. Но существует
        и другой вариант — разместить в теле класса методы <code className={styles.code}>set</code> и{' '}
        <code className={styles.code}>get</code>. Эти методы используют особый синтаксис, а при их вызове, после имени
        объекта следует написать только имя геттера или сеттера без вызова функции, т.е. без круглых скобок.
      </p>
      <CopyBlock
        text={`class Guest {
  // Собственные свойства класса размещаем в конструкторе
  constructor(name, roomNumber) {
    this.name = name;
    this.roomNumber = roomNumber;
  }

  // Используем геттеры и сеттеры для описания интерфейса доступа к свойствам
  get name() {
    return this.name;
  }

  set name(value) {
    this.name = value;
  }
}

const mango = new Guest('Mango', 26);

// обращение к get и set не требует вызова - т.е. без ()
console.log(mango.name); // Mango

mango.name = 'Mango the Fluffy';
console.log(mango.name); // Mango the Fluffy`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.4 - Статические свойства и методы</h3>
      <p className={styles.description}>
        Можно создавать собственные свойства класса и собственные методы класса для вызова по имени класса, без создания
        объекта. Такие свойства и методы называют статическими. Для их создания в классе перед свойством или методом
        нужно добавить служебное слово <code className={styles.code}>static</code>.{' '}
      </p>
      <p className={styles.description}>
        Конструктор класса тоже метод, но его нельзя делать статическим, это вызовет ошибку.{' '}
      </p>
      <CopyBlock
        text={`// Класс со статическими свойствами и методами
class Calc {
  // Класс-калькулятор для двух аргументов
  constructor() {}

  // Метод как замена свойству
  static get PI() {
    return 3.14;
  }

  // Статический метод +
  static add(...args) {
    return args.reduce((acc, next) => acc + next, 0);
  }

  // Статический метод *
  static mult(...args) {
    return args.reduce((acc, next) => acc * next, 1);
  }
}

console.log(Calc.PI); // 3.14
console.log(Calc.add(2, 3, 4)); // 9
console.log(Calc.mult(12, 3, 4)); // 144`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.5 - Наследование</h3>
      <p className={styles.description}>
        JavaScript позволяет реализовать наследование классов. Это означает, что мы можем создавать новые классы,
        которые наследуют все от другого класса, но при этом добавляют уникальную информацию для себя.{' '}
      </p>
      <CopyBlock
        text={`/*
 * Ключевое слово extends указывает на родительский класс,
 * чьи свойства будут унаследованы.
 */
class Child extends Parent {
  // ...
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Созданим базовый класс-родитель <code className={styles.code}>Animal</code>, от которого класс{' '}
        <code className={styles.code}>Dog</code> будет наследовать.{' '}
      </p>
      <CopyBlock
        text={`class Animal {
  constructor(name) {
    this.name = name;
  }

  move() {
    console.log(\`I, $ {this.name}, am moving!\`);
  }
}

class Dog extends Animal {
  constructor(name, breed) {
    // Вызвать конструктор Animal с аргументом name
    super(name);
    this.breed = breed;
  }

  bark() {
    console.log('woof!');
  }

  moveAndMakeSound() {
    super.move();
    this.bark();
  }
}

const dog = new Dog('Mango', 'shepherd');

dog.move(); // I, Mango, am moving!
dog.bark(); // woof!
dog.moveAndMakeSound(); // I, Mango, am moving! woof!`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        При наследовании через <code className={styles.code}>extends</code> формируется стандартная цепочка прототипов:
        методы <code className={styles.code}>Dog</code> находятся в <code className={styles.code}>Dog.prototype</code>,
        методы <code className={styles.code}>Animal</code> в <code className={styles.code}>Animal.prototype</code>, и
        они связаны через <code className={styles.code}>__proto__</code>.{' '}
      </p>
      <p className={styles.description}>
        Конструктор родителя наследуется автоматически. То есть, если в потомке не указан свой{' '}
        <code className={styles.code}>constructor</code>, то используется родительский. Если же у потомка свой{' '}
        <code className={styles.code}>constructor</code>, то, чтобы в нём вызвать конструктор родителя, используется
        метод <code className={styles.code}>super()</code> с аргументами для{' '}
        <code className={styles.code}>constructor</code> родителя.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Вызвать конструктор родителя можно только изнутри конструктора потомка. В частности,{' '}
          <code className={styles.code}>super()</code> нельзя вызвать из произвольного метода.
        </li>
        <li className={styles.list__item}>
          В конструкторе потомка мы обязаны вызвать <code className={styles.code}>super()</code> до первого обращения к
          ключевому слову <code className={styles.code}>this</code>. До вызова{' '}
          <code className={styles.code}>super()</code> не существует <code className={styles.code}>this</code>, так как
          по спецификации в этом случае именно <code className={styles.code}>super</code> инициализирует{' '}
          <code className={styles.code}>this</code>.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>super()</code> можно вызывать только в наследующем классе, иначе будет ошибка.
        </li>
        <li className={styles.list__item}>
          При наследовании, вызов конструктора родителя осуществляется через{' '}
          <code className={styles.code}>super(...args)</code>, вызов родительских методов через{' '}
          <code className={styles.code}>super.method(...args)</code>.
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.6 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://developers.google.com/web/updates/2018/12/class-fields"
            target="_blank"
            rel="noopener noreferrer"
          >
            Public and private class fields
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="http://exploringjs.com/es6/ch_classes.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Dr. Axel Rauschmayer - Exploring ES6, classes
          </a>
        </li>
      </ul>
    </section>
  </section>
))
